import pull from "lodash/pull";
import { WebsocketMessageCode } from "@/constants/websocket";
import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import { StoreModule } from "@/constants/store.ts";

export type TerminalEventHandler = (payload: string) => void;

export interface TerminalState {
  handlers: Map<string, TerminalEventHandler[]>;
  sessionId: number | null;
  commandInProgress: boolean;
  supportedCommands: TerminalSupportedCommand[];
  commandSequenceNumber: number;
  sessionOpened: boolean;
  lastCommandId: number | null;
  lastCommandResponseType: WebsocketMessageCode | null;
  lastCommandReceivedTime: number | null;
  failReason: string | null;
  websocketConnected: boolean;
}

export interface TerminalSupportedCommand {
  name: string;
  description: string;
}

const defaultTerminalState: TerminalState = {
  handlers: new Map(),
  sessionId: null,
  commandInProgress: false,
  supportedCommands: [],
  commandSequenceNumber: 1,
  sessionOpened: false,
  lastCommandId: null,
  lastCommandResponseType: null,
  lastCommandReceivedTime: null,
  failReason: null,
  websocketConnected: false,
};

export const useTerminalStore = defineStore(StoreModule.TERMINAL, {
  state: () => ({ ...defaultTerminalState }),
  getters: {
    isInInteractiveMode(state) {
      return (
        state.lastCommandResponseType === WebsocketMessageCode.SHELL_COMMAND_CONFIRMATION_REQUEST
      );
    },
  },
  actions: {
    setWebsocketConnected(payload: boolean) {
      this.websocketConnected = payload;
    },
    setSessionId(sessionId: number | string) {
      if (typeof sessionId === "string") {
        this.sessionId = parseInt(sessionId);
      } else {
        this.sessionId = sessionId;
      }
    },
    setCommandInProgress(commandInProgress: boolean) {
      this.commandInProgress = commandInProgress;
    },
    setCommandSequenceNumber(commandSequenceNumber: number) {
      this.commandSequenceNumber = commandSequenceNumber;
    },
    setSessionOpened(sessionOpened: boolean) {
      this.sessionOpened = sessionOpened;
    },
    setLastCommandId(commandId: number) {
      this.lastCommandId = commandId;
    },
    setFailReason(failReason: string) {
      this.failReason = failReason;
    },
    setLastCommandResponseType(lastCommandResponseType: WebsocketMessageCode) {
      this.lastCommandResponseType = lastCommandResponseType;
    },
    setLastCommandReceivedTime(lastCommandReceivedTime: number | null) {
      this.lastCommandReceivedTime = lastCommandReceivedTime;
    },
    resetState() {
      this.$reset();
    },
    addEventHandler({ type, handler }: { type: string; handler: TerminalEventHandler }) {
      let handlers = this.handlers.get(type);

      if (!handlers) handlers = [handler];
      else handlers.push(handler);

      this.handlers.set(type, handlers);
    },
    removeEventHandler({ type, handler }: { type: string; handler: TerminalEventHandler }) {
      const handlers = this.handlers.get(type);

      if (handlers) {
        pull(handlers, handler);
      }
    },
    emitEvent({ type, payload }: { type: string; payload: string }) {
      const handlers = this.handlers.get(type);

      if (handlers) {
        handlers.map((handler) => {
          handler(payload);
        });
      }
    },
    async getSupportedCommands(enrollmentCode: string) {
      try {
        const request = {
          ...api.getTerminalSupportedCommands(enrollmentCode),
        };

        const { data } = await axiosInstance.request(request);
        this.supportedCommands = data;
      } catch (error) {
        console.error(error);
      }
    },
    async sendTerminalCommand({
      enrollmentCode,
      sshCommand,
      script,
    }: {
      enrollmentCode: string;
      sshCommand: string;
      script?: File;
    }) {
      try {
        this.commandInProgress = true;

        const requestData = generateTerminalCommandFormData(
          {
            sessionId: this.sessionId!,
            sshCommand,
            commandSequenceNumber: this.commandSequenceNumber,
          },
          script
        );
        const request = {
          ...api.sendTerminalCommand(enrollmentCode),
          data: requestData,
        };

        const { data } = await axiosInstance.request(request);
        this.commandSequenceNumber += 1;
        this.lastCommandId = data.commandId;
      } catch (error) {
        console.error(error);
        this.commandInProgress = false;
      }
    },
    async getLastCommandResults() {
      const request = {
        ...api.getLastCommandResults(this.lastCommandId!, this.lastCommandReceivedTime),
      };

      return axiosInstance.request(request);
    },
    async getSessionStatus() {
      if (this.sessionId) {
        const request = {
          ...api.getSessionStatus(this.sessionId),
        };

        await axiosInstance.request(request);
        this.sessionOpened = true;
      }
    },
  },
});

function generateTerminalCommandFormData(
  {
    sshCommand,
    sessionId,
    commandSequenceNumber,
  }: { sshCommand: string; sessionId: number; commandSequenceNumber: number },
  script?: File
): FormData {
  const formData = new FormData();
  const data = new Blob([JSON.stringify({ sshCommand, sessionId, commandSequenceNumber })], {
    type: "application/json",
  });

  if (script) {
    const scriptBlob = new Blob([script], { type: "application/octet-stream" });
    formData.append("script", scriptBlob, script.name);
  }
  formData.append("command", data);
  return formData;
}
